import { useState, useCallback, useRef } from "react";
import { toast } from 'react-toastify';
import { FaMagic, FaPlus, FaTrash } from 'react-icons/fa';

import { parseSow, approveSowDraft, draftFromParseResult, parseCost, SowApplyError } from "../../api/sow";
import { SowDraftEditor } from "./SowDraftEditor";
import { ConfirmationDialog } from "../ConfirmationDialog";

// Tailwind 4's preflight sets `cursor: default` on <button>.
const PRIMARY_BUTTON =
  'px-4 py-2 bg-[#171717] text-white text-sm font-medium rounded-lg hover:bg-[#333] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

const SECONDARY_BUTTON =
  'px-4 py-2 text-sm font-medium text-[#555] bg-white border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] hover:border-[#ccc] hover:text-[#171717] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';

interface SowEstimatorProps {
  projectId: string;
  projectData: ProjectInterface;
  /** Live contents of the tab's scope-of-work editor — the only input there is. */
  sowText: string;
  /** Saves `sowText` onto the project. Called before an apply when it is dirty. */
  onPersistScope?: () => Promise<void>;
  canEdit: boolean;
  onApplied?: () => void;
}

export const SowEstimator = ({ projectId, projectData, sowText, onPersistScope, canEdit, onApplied }: SowEstimatorProps) => {
  const [isEstimating, setIsEstimating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [draft, setDraft] = useState<SOWDraft | null>(null);
  // Which destructive action is awaiting a second click: re-estimating over
  // edits, or applying to the project.
  const [confirming, setConfirming] = useState<'estimate' | 'approve' | null>(null);

  // The draft as parsed, to tell an untouched result from an edited one.
  const pristine = useRef('');
  const isEdited = draft !== null && JSON.stringify(draft) !== pristine.current;

  const trimmed = sowText.trim();
  const hasText = trimmed.length > 0;
  // Estimating works on the editor's text, saved or not.
  const scopeIsDirty = sowText !== (projectData?.description || '');

  const resetResult = () => { setDraft(null); setErrors([]); setConfirming(null); };

  const handleEstimate = async () => {
    if (!hasText) return;

    // Re-estimating throws the reviewed draft away; make that a deliberate act.
    if (isEdited && confirming !== 'estimate') {
      setConfirming('estimate');
      return;
    }

    setConfirming(null);
    setIsEstimating(true);
    setErrors([]);
    try {
      const result = await parseSow({ text: trimmed, project_hint: projectData?.name });
      const next = draftFromParseResult(result);
      pristine.current = JSON.stringify(next);
      setDraft(next);
    } catch (e) {
      setErrors([e instanceof Error ? e.message : 'Failed to estimate the scope of work']);
      setDraft(null);
    } finally {
      setIsEstimating(false);
    }
  };

  const patchDraft = useCallback((fn: (d: SOWDraft) => SOWDraft) => {
    setDraft(prev => (prev ? fn(prev) : prev));
  }, []);

  // ---- estimation block edits ---------------------------------------------
  const patchEstimation = (field: keyof SOWDraftEstimation, value: string) =>
    patchDraft(d => ({ ...d, estimation: { ...d.estimation, [field]: value } }));

  const updateAssumption = (idx: number, value: string) =>
    patchDraft(d => ({ ...d, estimation: { ...d.estimation, assumptions: d.estimation.assumptions.map((a, i) => (i === idx ? value : a)) } }));

  const addAssumption = () =>
    patchDraft(d => ({ ...d, estimation: { ...d.estimation, assumptions: [...d.estimation.assumptions, ''] } }));

  const removeAssumption = (idx: number) =>
    patchDraft(d => ({ ...d, estimation: { ...d.estimation, assumptions: d.estimation.assumptions.filter((_, i) => i !== idx) } }));

  // ---- approve ------------------------------------------------------------
  /** Apply writes phases, tasks and the budget straight onto the project, so it
   *  is confirmed in a modal against what is already there. */
  const applyDraft = async () => {
    if (!draft || !projectId) return;

    setConfirming(null);
    setIsApproving(true);
    setErrors([]);
    try {
      // Apply only validates the top-level project fields (workflow step 11), so
      // the scope text the estimate came from is saved through the project
      // endpoint first; the apply body then carries the same text rather than
      // the stale saved one.
      if (scopeIsDirty && onPersistScope) await onPersistScope();

      const report = await approveSowDraft(projectId, draft, { ...projectData, description: sowText });
      toast.success(
        `Applied ${report.phases} phase(s), ${report.items} task(s), ${report.actionItems} action item(s).`
      );
      resetResult();
      onApplied?.();
    } catch (e) {
      // Keep the draft so the user can correct and retry (workflow step 16).
      setErrors(e instanceof SowApplyError ? e.details : [e instanceof Error ? e.message : 'Failed to apply the scope of work']);
    } finally {
      setIsApproving(false);
    }
  };

  const busy = isEstimating || isApproving;

  // The parser is extractive: a scope that never mentions crew or cost comes
  // back with an empty estimation block rather than a guess.
  const isEstimateEmpty =
    draft !== null &&
    !draft.estimation.estimatedPeople &&
    !draft.estimation.totalCost &&
    !draft.estimation.currency;

  return (
    <div className="space-y-5">
      {/* Action bar, directly under the scope-of-work editor */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleEstimate}
          disabled={busy || !hasText}
          className={`${PRIMARY_BUTTON} flex items-center justify-center gap-2`}
        >
          <FaMagic className="w-3 h-3" />
          {isEstimating ? 'Estimating…' : 'Estimate with AI'}
        </button>

        <span className="text-xs text-[#aaa]">
          {hasText
            ? 'Reads the scope of work above and drafts phases, tasks and a cost estimate.'
            : 'Write a scope of work above to estimate it.'}
        </span>

        {draft && (
          <button
            type="button"
            onClick={resetResult}
            disabled={busy}
            className={`${SECONDARY_BUTTON} ml-auto px-3 py-1.5 text-xs`}
          >
            Discard result
          </button>
        )}
      </div>

      {confirming === 'estimate' && (
        <div className="text-sm text-[#555] bg-[#fffbeb] border border-[#fde68a] rounded-lg p-3">
          <p className="font-medium text-[#171717]">Replace your edited estimate?</p>
          <p className="mt-1 text-xs">Estimating again discards the changes you made below.</p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={handleEstimate} className={`${PRIMARY_BUTTON} px-3 py-1.5 text-xs`}>
              Estimate anyway
            </button>
            <button type="button" onClick={() => setConfirming(null)} className={`${SECONDARY_BUTTON} px-3 py-1.5 text-xs`}>
              Keep my edits
            </button>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          {errors.length === 1 ? (
            errors[0]
          ) : (
            <ul className="list-disc pl-4 space-y-1">
              {errors.map((message, i) => <li key={i}>{message}</li>)}
            </ul>
          )}
        </div>
      )}

      {draft && (
        <div className="space-y-5 pt-1">
          {!canEdit && (
            <p className="text-xs text-[#aaa]">Your role does not allow applying an estimate to this project.</p>
          )}

          {/* Estimation summary */}
          <div className="bg-[#fafafa] border border-[#eaeaea] rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-[#171717]">Estimate</h4>
              {draft.estimation.confidence != null && (
                <span className="text-xs text-[#888]">Confidence: {Math.round(draft.estimation.confidence * 100)}%</span>
              )}
            </div>
            {isEstimateEmpty && (
              <p className="text-xs text-[#888] bg-white border border-[#eaeaea] rounded-md p-2">
                The parser only picks up figures stated in the scope of work, and this one gave none.
                Add the crew size, cost and currency below, or mention them in the text and estimate again.
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="text-xs text-[#888]">
                Contracted workers
                <input type="number" min="0" value={draft.estimation.estimatedPeople}
                  onChange={e => patchEstimation('estimatedPeople', e.target.value)}
                  className="mt-1 w-full px-2.5 py-1.5 text-sm text-[#171717] bg-white border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#888]" />
              </label>
              <label className="text-xs text-[#888]">
                Total cost <span className="text-[#bbb]">(→ budget)</span>
                <input type="text" inputMode="decimal" value={draft.estimation.totalCost}
                  onChange={e => patchEstimation('totalCost', e.target.value)}
                  className="mt-1 w-full px-2.5 py-1.5 text-sm text-[#171717] bg-white border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#888]" />
              </label>
              <label className="text-xs text-[#888]">
                Currency
                <input type="text" value={draft.estimation.currency}
                  onChange={e => patchEstimation('currency', e.target.value)}
                  className="mt-1 w-full px-2.5 py-1.5 text-sm text-[#171717] bg-white border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#888]" />
              </label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-[#888]">Assumptions</span>
                <button
                  type="button"
                  onClick={addAssumption}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#555] bg-white border border-[#eaeaea] rounded-md hover:bg-[#fafafa] hover:border-[#ccc] hover:text-[#171717] transition-colors cursor-pointer"
                >
                  <FaPlus className="w-2.5 h-2.5" /> Add
                </button>
              </div>
              {draft.estimation.assumptions.length === 0 && (
                <p className="text-xs text-[#bbb]">No assumptions returned.</p>
              )}
              {draft.estimation.assumptions.map((a, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={a} onChange={e => updateAssumption(i, e.target.value)}
                    className="flex-1 px-2.5 py-1.5 text-sm bg-white border border-[#eaeaea] rounded-md focus:outline-none focus:border-[#888]" />
                  <button type="button" aria-label={`Remove assumption ${i + 1}`} onClick={() => removeAssumption(i)} className="p-1.5 rounded text-[#aaa] hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"><FaTrash className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#aaa]">
              Only total cost is saved (to the project budget). Workers, currency, and assumptions are shown for review.
            </p>
          </div>

          {/* Phases + tasks */}
          <SowDraftEditor draft={draft} patch={patchDraft} />

          {/* Approve */}
          {canEdit && (
            <div className="space-y-3 pt-2 border-t border-[#eaeaea]">
              <div className="flex items-center justify-end gap-2">
                <span className="mr-auto text-xs text-[#aaa]">
                  Applying to <span className="font-medium text-[#555]">{projectData?.name}</span>
                </span>
                <button type="button" onClick={resetResult} disabled={isApproving} className={SECONDARY_BUTTON}>
                  Discard
                </button>
                <button type="button" onClick={() => setConfirming('approve')} disabled={isApproving}
                  className={`${PRIMARY_BUTTON} min-w-[110px] flex items-center justify-center`}>
                  {isApproving ? 'Applying…' : 'Approve'}
                </button>
              </div>

              <ConfirmationDialog
                isOpen={confirming === 'approve'}
                onClose={() => setConfirming(null)}
                onConfirm={applyDraft}
                isLoading={isApproving}
                title="Apply this estimate?"
                message={applyMessage(draft, scopeIsDirty, projectData?.name)}
                confirmText="Confirm apply"
                cancelText="Cancel"
                confirmButtonClass="bg-[#171717] hover:bg-[#333]"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** What the apply is about to do, in one sentence, for the confirmation modal. */
const applyMessage = (draft: SOWDraft, scopeIsDirty: boolean, projectName?: string): string => {
  const clauses = [
    `adds ${draft.phases.length} phase(s), ${draft.items.length} task(s) and ${draft.actionItems.length} action item(s) to the project`
  ];
  if (parseCost(draft.estimation.totalCost) !== null) {
    clauses.push(`sets the budget to ${draft.estimation.totalCost.trim()}`);
  }
  if (scopeIsDirty) clauses.push('saves your unsaved scope-of-work edits');

  const list =
    clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(', ')} and ${clauses[clauses.length - 1]}`;

  const target = projectName?.trim() ? `"${projectName.trim()}"` : 'this project';
  return `Applying to ${target}. This ${list}. Existing phases and tasks on this project may be affected.`;
};

export default SowEstimator;
